/**
 * "Sua sessão expirou" sobre a tela (Estados.dc.html): a senha do mesmo e-mail e,
 * se a conta pedir, o código do aplicativo. Com a entrada concluída, os pedidos
 * que esperavam são refeitos e a tela segue de onde parou. "Sair" encerra.
 *
 * Esc e clique fora NÃO fecham: sem sessão, a tela não pode seguir. A entrada só
 * vale para a mesma pessoa (o id devolvido tem de ser o guardado).
 * @module Casca/SessaoExpirada
 */
import React, { useEffect, useState } from 'react';
import { Botao, Campo, Dialogo } from '../egora';
import CampoSenha from '../features/auth/CampoSenha';
import { EntradaErro } from '../features/auth/EntradaMoldura';
import apiFetch, { clearAuth, setStoredUser, setTokens } from '../../apiClient';
import { ouvirSessaoExpirada } from './sessaoExpirada';
import '../features/auth/entrada.css';

const mensagem = (dados, padrao) => (typeof dados?.detail === 'string' ? dados.detail : dados?.error?.message) || padrao;

export default function SessaoExpirada() {
  const [pedido, setPedido] = useState(null);
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [desafio, setDesafio] = useState(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => ouvirSessaoExpirada((novo) => {
    setPedido((atual) => {
      if (atual) { novo.desistir(new Error('Sessão expirada')); return atual; }
      return novo;
    });
    setSenha(''); setCodigo(''); setDesafio(null); setErro('');
  }), []);

  if (!pedido) return null;
  const { usuario } = pedido;

  const concluir = (dados) => {
    if (!dados?.access_token || (dados.usuario?.id != null && usuario.id != null && dados.usuario.id !== usuario.id)) {
      setErro('Não foi possível continuar com esta conta. Saia e entre de novo.');
      return;
    }
    setTokens(dados.access_token, dados.refresh_token);
    if (dados.usuario) setStoredUser(dados.usuario);
    setSenha(''); setCodigo(''); setDesafio(null);
    pedido.concluir(dados.access_token);
    setPedido(null);
  };

  const sair = () => {
    pedido.desistir(new Error('Sessão encerrada'));
    setPedido(null);
    clearAuth();
    window.location.href = '/login';
  };

  const continuar = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      if (!desafio) {
        const res = await apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify({ email: usuario.email, senha }) });
        const dados = await res.json().catch(() => ({}));
        setSenha('');
        if (!res.ok) { setErro(res.status === 401 ? 'A senha não confere.' : mensagem(dados, 'Não foi possível entrar agora.')); return; }
        if (dados.status === 'mfa_required' && dados.challenge_token) { setDesafio(dados.challenge_token); return; }
        concluir(dados);
      } else {
        const res = await apiFetch('/api/auth/verify-mfa', { method: 'POST', headers: { Authorization: `Bearer ${desafio}` }, body: JSON.stringify({ codigo: codigo.trim() }) });
        const dados = await res.json().catch(() => ({}));
        setCodigo('');
        if (!res.ok) {
          const texto = mensagem(dados, 'Código inválido.');
          if (res.status === 401 && /expirou|Faça o login|Muitas tentativas/i.test(texto)) setDesafio(null);
          setErro(texto);
          return;
        }
        concluir(dados);
      }
    } catch (_) {
      setErro('Não foi possível falar com o servidor. Verifique sua conexão.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialogo aberto onFechar={() => {}} titulo="Sua sessão expirou"
      texto="Por segurança, a sessão foi encerrada. Entre de novo para continuar de onde parou: nada do que estava na tela se perdeu.">
      <form className="entrada__caixa sessao-expirada" onSubmit={continuar} noValidate>
        <EntradaErro>{erro}</EntradaErro>
        {!desafio ? (
          <CampoSenha rotulo={`Senha de ${usuario.email}`} valor={senha} aoMudar={setSenha} autoFocus />
        ) : (
          <Campo rotulo="Código do aplicativo ou código reserva">
            {(p) => <input {...p} className="sj-campo__entrada sj-num entrada__codigo" autoComplete="one-time-code" maxLength={39} value={codigo} onChange={(e) => setCodigo(e.target.value)} autoFocus />}
          </Campo>
        )}
        <div className="sj-dialogo__acoes">
          <Botao variante="secundario" onClick={sair}>Sair</Botao>
          <Botao type="submit" carregando={enviando} disabled={desafio ? !codigo.trim() : !senha}>Continuar</Botao>
        </div>
      </form>
    </Dialogo>
  );
}
